// The register: every deliverable the Project Assignment names, by phase, with
// submission and acceptance, and the days each has waited for the client's review.
import { Fragment } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { deliverableLabel, useDeliverables, type DeliverableRow, type DeliverableStatus } from '@/lib/deliverables';
import type { Program } from '@/lib/types';
import { cn, fmtDate } from '@/lib/utils';
import { Select } from '@/components/ui/field';
import { Badge, Card, PageHeader, Spinner } from '@/components/ui/misc';

const STATUS_TONE: Record<DeliverableStatus, 'neutral' | 'info' | 'warn' | 'ok' | 'bad' | 'brand'> = {
  NOT_STARTED: 'neutral',
  DRAFTING: 'info',
  INTERNAL_REVIEW: 'info',
  SUBMITTED: 'warn',
  REVISIONS_REQUESTED: 'bad',
  ACCEPTED: 'ok',
};

export function DeliverableStatusBadge({ status, client }: { status: DeliverableStatus; client: string }) {
  return <Badge tone={STATUS_TONE[status]}>{deliverableLabel(status, client)}</Badge>;
}

/** Days awaiting review; warm at a week, hot at two. */
export function AwaitingDays({ days }: { days: number | null }) {
  if (days === null) return <span className="text-subtle">·</span>;
  return (
    <span
      className={cn('tabular font-medium', days >= 14 ? 'text-bad' : days >= 7 ? 'text-warn' : 'text-text')}
      title="Days since submitted to the client for review"
    >
      {days} {days === 1 ? 'day' : 'days'}
    </span>
  );
}

export function DeliverablesPage() {
  const program = useOutletContext<Program>();
  const q = useDeliverables(program.id);
  const [params, setParams] = useSearchParams();
  const filter = params.get('show') ?? '';
  const set = (v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set('show', v);
    else next.delete('show');
    setParams(next, { replace: true });
  };

  if (!q.data) return <Spinner className="h-full" />;
  const all = q.data;
  const pa = all.filter((d) => d.isProgramLevel);
  const awaiting = all.filter((d) => d.status === 'SUBMITTED');
  const rows = all.filter((d) =>
    filter === 'pa'
      ? d.isProgramLevel
      : filter === 'awaiting'
        ? d.status === 'SUBMITTED'
        : filter === 'mine'
          ? d.canAccept
          : true,
  );
  const phases = [...new Map(rows.map((d) => [d.phase.id, d.phase])).values()].sort((a, b) => a.number - b.number);

  return (
    <div className="mx-auto max-w-[1300px] px-6 py-5">
      <PageHeader
        title="Deliverables"
        description="The register of what the Project Assignment requires, and its formal record of submission and acceptance."
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Stat
          label="PA §4 deliverables accepted"
          value={`${pa.filter((d) => d.status === 'ACCEPTED').length} of ${pa.length}`}
        />
        <Stat
          label="All deliverables accepted"
          value={`${all.filter((d) => d.status === 'ACCEPTED').length} of ${all.length}`}
        />
        <Stat
          label={`Awaiting ${program.clientShortName} review`}
          value={String(awaiting.length)}
          note={awaiting.length ? `Longest: ${Math.max(...awaiting.map((d) => d.daysAwaiting ?? 0))} days` : undefined}
        />
      </div>
      <div className="mb-3 flex items-center gap-2">
        <Select aria-label="Show" className="h-9 w-64 text-[13px]" value={filter} onChange={(e) => set(e.target.value)}>
          <option value="">All deliverables</option>
          <option value="pa">PA §4 program deliverables ({pa.length})</option>
          <option value="awaiting">Awaiting {program.clientShortName} review</option>
          <option value="mine">Awaiting my acceptance</option>
        </Select>
        <span className="ml-auto text-xs text-muted tabular">{rows.length} shown</span>
      </div>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-[13px]">
            <thead className="border-b border-border text-left text-[11px] font-medium uppercase tracking-wide text-subtle">
              <tr>
                <th className="px-4 py-2">Deliverable</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Target</th>
                <th className="px-2 py-2">Version</th>
                <th className="px-2 py-2">Submitted</th>
                <th className="px-2 py-2">Awaiting review</th>
                <th className="px-2 py-2">Approver</th>
              </tr>
            </thead>
            <tbody>
              {phases.map((ph) => (
                <Fragment key={ph.id}>
                  <tr className="border-b border-border bg-surface-2/60">
                    <td colSpan={7} className="px-4 py-1.5 text-[12.5px] font-semibold">
                      {ph.number}. {ph.name}
                    </td>
                  </tr>
                  {rows
                    .filter((d) => d.phase.id === ph.id)
                    .map((d) => (
                      <Row key={d.id} d={d} program={program} />
                    ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          {!rows.length && <p className="py-10 text-center text-sm text-muted">Nothing to show.</p>}
        </div>
      </Card>
    </div>
  );
}

function Row({ d, program }: { d: DeliverableRow; program: Program }) {
  return (
    <tr className="border-b border-border hover:bg-surface-2/40">
      <td className="px-4 py-2">
        <Link to={d.id} className="font-medium hover:underline">
          {d.name}
        </Link>
        <span className="ml-2 inline-flex gap-1 align-middle">
          {d.isProgramLevel && <Badge tone="brand">PA §4</Badge>}
          {d.canAccept && <Badge tone="warn">Your acceptance</Badge>}
        </span>
        {d.aliases.length > 0 && <p className="text-xs text-muted">Also: {d.aliases.join(', ')}</p>}
      </td>
      <td className="px-2 py-2">
        <DeliverableStatusBadge status={d.status} client={program.clientShortName} />
      </td>
      <td className="px-2 py-2 text-xs tabular text-muted">{d.targetDate ? fmtDate(d.targetDate) : '·'}</td>
      <td className="px-2 py-2 text-xs tabular text-muted">{d.currentVersion ? `v${d.currentVersion}` : '·'}</td>
      <td className="px-2 py-2 text-xs tabular text-muted">
        {d.submittedAt ? fmtDate(d.submittedAt.slice(0, 10)) : '·'}
      </td>
      <td className="px-2 py-2 text-xs">
        <AwaitingDays days={d.daysAwaiting} />
      </td>
      <td className="px-2 py-2 text-xs text-muted">
        {d.approver ? (
          <>
            {d.approver.name}
            {d.approver.delegated && <span className="text-subtle"> (delegated)</span>}
          </>
        ) : (
          'No approver set'
        )}
      </td>
    </tr>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <Card className="px-4 py-3">
      <p className="text-xs text-muted">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tabular">{value}</p>
      {note && <p className="text-xs text-muted">{note}</p>}
    </Card>
  );
}
